// The same action fixtures are exercised by the Flutter adapter and RTEM intake.
export const createTelemetryUuid = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues)
    globalThis.crypto.getRandomValues(bytes);
  else
    bytes.forEach((_, index) => {
      bytes[index] = Math.floor(Math.random() * 256);
    });
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0")
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const supportsTelemetryV2 = (base) => {
  if (
    !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(base?.contentKey || "") ||
    base.contentKey.length > 256
  )
    return false;
  if (base.mode === "live")
    return (
      base.contentKind === "live" && base.season == null && base.episode == null
    );
  if (base.mode !== "vod") return false;
  const episodic = ["episode", "series", "program", "sport"].includes(
    base.contentKind
  );
  if (!episodic && !["movie", "animation", "music"].includes(base.contentKind))
    return false;
  const coordinate = (value) =>
    Number.isInteger(value) && value > 0 && value <= 65535;
  return episodic
    ? coordinate(base.season) && coordinate(base.episode)
    : base.season == null && base.episode == null;
};

export class PlaybackTelemetry {
  constructor({
    base,
    emit,
    sessionId = createTelemetryUuid(),
    clock = () => performance.now(),
    createId = createTelemetryUuid,
  }) {
    this.base = { ...base, playbackSessionId: sessionId };
    this.emit = emit;
    this.clock = clock;
    this.createId = createId;
    this.version = supportsTelemetryV2(base) ? 2 : 1;
    this.sequence = 0;
    this.position = 0;
    this.duration = null;
    this.rate = 1;
    this.state = "idle";
    this.started = false;
    this.ended = false;
    this.range = null;
    this.anchorAt = null;
    this.seekWasPlaying = false;
  }

  send(type, extra = {}, options = {}) {
    if (this.ended) return null;
    return this.emit(
      {
        ...this.base,
        eventId: this.createId(),
        timestamp: Date.now(),
        schemaVersion: this.version,
        eventType: type,
        positionSec: this.position,
        ...(this.duration > 0 ? { durationSec: this.duration } : {}),
        ...(this.version === 2
          ? {
              eventSequence: this.sequence++,
              playbackRate: this.rate,
              playbackState: this.state,
            }
          : {}),
        ...extra,
      },
      options
    );
  }

  adTimelineContext(position = this.position) {
    if (this.version !== 2 || this.base.mode !== "vod" || this.ended || !Number.isFinite(position) || position < 0) return null;
    const episodic = this.base.season != null;
    return Object.freeze({
      schemaVersion: 1,
      contentKey: this.base.contentKey,
      contentKind: episodic ? "episode" : "movie",
      ...(episodic ? { season: this.base.season, episode: this.base.episode } : {}),
      playbackSessionId: this.base.playbackSessionId,
      contentPositionSec: position,
    });
  }

  start() {
    return this.send("session_start");
  }
  setDuration(value) {
    if (Number.isFinite(value) && value > 0) this.duration = value;
  }

  play() {
    if (this.ended || this.state === "playing") return;
    this.state = "playing";
    this.anchorAt = this.clock();
    const type = this.started ? "resume" : "playback_start";
    this.started = true;
    return this.send(type);
  }

  observe(value) {
    if (
      this.ended ||
      !Number.isFinite(value) ||
      value < 0 ||
      (this.duration && value > this.duration)
    )
      return;
    const now = this.clock();
    const delta = value - this.position;
    const wall = this.anchorAt === null ? 0 : (now - this.anchorAt) / 1000;
    if (this.state === "playing" && this.version === 2) {
      if (
        delta < 0 ||
        (delta > 0 &&
          (wall <= 0 || wall > 15 || delta > this.rate * wall + 1)) ||
        (delta > 0 &&
          this.range &&
          value - this.range.from > this.rate * (this.range.wall + wall) + 1)
      ) {
        this.flush();
        const from = this.position;
        this.position = value;
        this.state = "seeking";
        this.send("seek", {
          metadata: {
            fromPositionSec: from,
            toPositionSec: value,
            inferred: true,
          },
        });
        this.state = "playing";
      } else if (delta > 0) {
        if (this.range && this.range.wall + wall > 120) this.flush();
        if (!this.range) this.range = { from: this.position, to: value, wall };
        else {
          this.range.to = value;
          this.range.wall += wall;
        }
      } else if (this.range) {
        // A stalled playhead does not add wall time or bridge a later discontinuity.
        this.flush();
      }
    }
    this.position = value;
    this.anchorAt = this.state === "playing" ? now : null;
  }

  flush(options = {}) {
    if (!this.range || this.ended) return null;
    const range = this.range;
    this.range = null;
    return this.send(
      "heartbeat",
      {
        positionSec: range.to,
        playbackState: "playing",
        playedFromSec: range.from,
        playedToSec: range.to,
        playedWallTimeSec: range.wall,
        heartbeatIntervalSec: Math.min(120, Math.max(1, Math.ceil(range.wall))),
      },
      options
    );
  }

  heartbeat() {
    if (this.state !== "playing" || this.ended) return;
    return this.version === 2
      ? this.flush()
      : this.send("heartbeat", { heartbeatIntervalSec: 10 });
  }

  transition(state, type, extra = {}, options = {}) {
    if (this.ended) return;
    this.flush(options);
    if (state === "paused" || state === "advertisement")
      this.seekWasPlaying = false;
    this.state = state;
    this.anchorAt = state === "playing" ? this.clock() : null;
    return this.send(type, extra, options);
  }

  seekStart() {
    this.seekWasPlaying = this.state === "playing";
    return this.transition("seeking", "seek", {
      metadata: { fromPositionSec: this.position },
    });
  }

  seekEnd(value) {
    this.observe(value);
    this.state = this.seekWasPlaying ? "playing" : "paused";
    this.anchorAt = this.state === "playing" ? this.clock() : null;
    this.seekWasPlaying = false;
  }

  setRate(value) {
    if (
      !Number.isFinite(value) ||
      value < 0.25 ||
      value > 8 ||
      value === this.rate
    )
      return;
    this.flush();
    this.rate = value;
    this.anchorAt = this.state === "playing" ? this.clock() : null;
    return this.send("quality_change", { metadata: { playbackRate: value } });
  }

  end(reason = "user_exit", options = {}) {
    if (this.ended) return;
    this.transition(
      "ended",
      "session_end",
      this.version === 2 ? { endReason: reason } : {},
      options
    );
    this.ended = true;
  }
}
